import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Snackbar from '../components/Snackbar';
import MenuButton from '../components/MenuButton';
import trainingService, { TrainingComment, TrainingDayView, TrainingEntryView, TrainingWeekResponse } from '../services/trainingService';
import categoryService, { ExerciseCategory } from '../services/categoryService';
import { linkify } from '../utils/linkify';
import TrainingCommentModal from '../components/TrainingCommentModal';
import { useAppSelector } from '../store/hooks';
import axiosInstance from '../config/axiosInstance';

type Race = {
    id: string;
    raceType: string;
    date: string; // YYYY-MM-DD
    location: string;
    status: string;
};

type EditableSession = {
    id?: string; // present only for sessions that already exist server-side
    title: string;
    description: string;
    activityLink: string;
    sport: string;
    // Read-only athlete completion data (never sent back on save).
    completed?: boolean;
    durationHours?: number | null;
};

// Sentinel value for the "add a new category" option in the sport picker.
const ADD_CATEGORY = '__add__';

const blankSession = (): EditableSession => ({ title: '', description: '', activityLink: '', sport: '' });

/**
 * Local stand-in for the placeholder entry the backend created to host a new
 * day-comment thread. Only the fields the grid reads are meaningful; it is never
 * sent back on save, and the next week load replaces it with the real row.
 */
const placeholderEntry = (
    comment: TrainingComment,
    date: string,
    userId: string,
): TrainingEntryView => ({
    id: comment.entryId,
    userId,
    trainerId: '',
    trainingDate: date,
    title: '',
    description: null,
    activityLink: null,
    userComment: null,
    sport: null,
    isPlaceholder: true,
    completed: false,
    durationHours: null,
    createdAt: comment.createdAt,
    updatedAt: comment.createdAt,
});

/** Sent by the notification bell when it is clicked while this page is open. */
type NotificationTarget = { userId: string; entryId: string; weekDate: string | null; message: string };

const getWeekStartMonday = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
};

const toDateOnlyString = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatDateNumber = (date: string) => new Date(`${date}T00:00:00`).getDate();
const formatMonthYear = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleString('en-US', { month: 'short', year: 'numeric' });

const getWeekNumber = (d: Date) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const UserTrainingPlan: React.FC = () => {
    const { userId = '' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location.state as { openEntryId?: string; weekDate?: string } | null;
    const initialWeek = navState?.weekDate
        ? getWeekStartMonday(new Date(`${navState.weekDate}T00:00:00`))
        : getWeekStartMonday(new Date());

    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(initialWeek);
    const [weekData, setWeekData] = useState<TrainingWeekResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [editableDays, setEditableDays] = useState<Record<string, EditableSession[]>>({});
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [commentsByEntryId, setCommentsByEntryId] = useState<Record<string, TrainingComment[]>>({});
    const [categories, setCategories] = useState<ExerciseCategory[]>([]);
    const [races, setRaces] = useState<Race[]>([]);
    // The add-category prompt targets one specific session cell.
    const [addingCategoryFor, setAddingCategoryFor] = useState<{ date: string; index: number } | null>(null);
    const [newCategoryLabel, setNewCategoryLabel] = useState('');
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [showManageCategories, setShowManageCategories] = useState(false);
    const [modalEntryId, setModalEntryId] = useState<string | null>(null);
    const [focusEntryId, setFocusEntryId] = useState<string | null>(null);
    const [pendingNotification, setPendingNotification] = useState<NotificationTarget | null>(null);
    const [inputByEntryId, setInputByEntryId] = useState<Record<string, string>>({});
    const [sendingByEntryId, setSendingByEntryId] = useState<Record<string, boolean>>({});
    const [programLabel, setProgramLabel] = useState('');
    const currentUser = useAppSelector((state) => state.auth.user);
    const pendingOpenEntryRef = useRef<string | null>(navState?.openEntryId ?? null);

    const weekDateParam = useMemo(() => toDateOnlyString(currentWeekStart), [currentWeekStart]);

    const sessionsFromDay = (day: TrainingDayView): EditableSession[] => {
        // Placeholders hold a comment thread, not an exercise. They are edited
        // through the blank session row so the trainer can turn one into a real
        // session — carrying its id keeps the existing comments attached.
        const real = day.entries.filter((e) => !e.isPlaceholder);
        if (real.length === 0) {
            const placeholder = day.entries.find((e) => e.isPlaceholder);
            // A past empty day still shows its thread (fields stay locked) so the
            // trainer can read and reply to every comment, past days included.
            return [placeholder ? { ...blankSession(), id: placeholder.id } : blankSession()];
        }
        return real.map((entry) => ({
            id: entry.id,
            title: entry.title || '',
            description: entry.description || '',
            activityLink: entry.activityLink || '',
            sport: entry.sport || '',
            completed: entry.completed,
            durationHours: entry.durationHours,
        }));
    };

    const loadWeek = async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const response = await trainingService.getUserWeek(userId, weekDateParam);
            setWeekData(response);
            setProgramLabel(response.programLabel || '');

            const state: Record<string, EditableSession[]> = {};
            response.days.forEach((day) => {
                state[day.date] = sessionsFromDay(day);
            });
            setEditableDays(state);

            const entryIds = response.days.flatMap((d) => d.entries.map((e) => e.id));
            if (entryIds.length > 0) {
                const results = await Promise.all(
                    entryIds.map((id) => trainingService.getComments(id).then((comments) => ({ id, comments })))
                );
                const map: Record<string, TrainingComment[]> = {};
                results.forEach(({ id, comments }) => { map[id] = comments; });
                setCommentsByEntryId(map);
            }

            // Coming from a notification: jump to that entry's reply box.
            if (pendingOpenEntryRef.current) {
                setFocusEntryId(pendingOpenEntryRef.current);
                pendingOpenEntryRef.current = null;
            }
        } catch (error: any) {
            const message = error?.response?.data?.message;
            const errorText = Array.isArray(message) ? message.join(', ') : message || 'Failed to load weekly training.';
            setSnackbar({ message: errorText, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadWeek();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, weekDateParam]);

    /** True when the grid or program label differs from the last loaded/saved week. */
    const hasUnsavedChanges = () => {
        if (!weekData) return false;
        if (programLabel !== (weekData.programLabel || '')) return true;
        return weekData.days.some(
            (day) => JSON.stringify(editableDays[day.date] ?? []) !== JSON.stringify(sessionsFromDay(day)),
        );
    };

    const goToNotification = async (target: NotificationTarget) => {
        setPendingNotification(null);
        const targetWeek = getWeekStartMonday(target.weekDate ? new Date(`${target.weekDate}T00:00:00`) : new Date());
        if (target.userId === userId && toDateOnlyString(targetWeek) === weekDateParam) {
            // Same week is already on screen: pull in the new comment and jump to it.
            const comments = await trainingService.getComments(target.entryId);
            setCommentsByEntryId((prev) => ({ ...prev, [target.entryId]: comments }));
            setFocusEntryId(target.entryId);
            return;
        }
        // loadWeek reruns on the user/week change and focuses this entry.
        pendingOpenEntryRef.current = target.entryId;
        setCurrentWeekStart(targetWeek);
        navigate(`/users/${target.userId}/training`, {
            state: { openEntryId: target.entryId, weekDate: target.weekDate },
        });
    };

    useEffect(() => {
        const onNotification = (e: Event) => {
            const target = (e as CustomEvent<NotificationTarget>).detail;
            const targetWeek = getWeekStartMonday(target.weekDate ? new Date(`${target.weekDate}T00:00:00`) : new Date());
            // Ask only when leaving would lose edits. Saving also emails the
            // athlete their week, so it must not run when nothing changed.
            const sameWeek = target.userId === userId && toDateOnlyString(targetWeek) === weekDateParam;
            if (sameWeek || !hasUnsavedChanges()) {
                goToNotification(target);
            } else {
                setPendingNotification(target);
            }
        };
        window.addEventListener('open-training-notification', onNotification);
        return () => window.removeEventListener('open-training-notification', onNotification);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, weekDateParam, weekData, editableDays, programLabel]);

    const handleSaveAndOpenNotification = async () => {
        if (!pendingNotification) return;
        if (await handleSaveWeek()) goToNotification(pendingNotification);
    };

    // The grid only renders once loading ends, so wait for it before focusing.
    useEffect(() => {
        if (loading || !focusEntryId) return;
        const input = document.getElementById(`comment-input-${focusEntryId}`);
        input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input?.focus({ preventScroll: true });
        setFocusEntryId(null);
    }, [loading, focusEntryId]);

    const loadCategories = async () => {
        try {
            setCategories(await categoryService.list());
        } catch {
            // Non-critical: the picker still works with whatever's already loaded.
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    const loadRaces = async () => {
        if (!userId) return;
        try {
            const res = await axiosInstance.get(`/races/users/${userId}`);
            setRaces(res.data ?? []);
        } catch {
            // Non-critical: week planning still works without race markers.
        }
    };

    useEffect(() => {
        loadRaces();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Close the categories menu on an outside click or Escape, matching the
    // row action menu on the users list.
    useEffect(() => {
        if (!showManageCategories) return;

        const handlePointerDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-categories-menu]')) {
                setShowManageCategories(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowManageCategories(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showManageCategories]);

    // Races indexed by date for quick per-day lookup in the planning grid.
    const racesByDate = useMemo(() => {
        const map: Record<string, Race[]> = {};
        for (const race of races) {
            (map[race.date] ||= []).push(race);
        }
        return map;
    }, [races]);

    // Header strip: races still ahead of the athlete, soonest first.
    const upcomingRaces = useMemo(() => {
        const today = toDateOnlyString(new Date());
        return races
            .filter((r) => r.date >= today && r.status !== 'completed' && r.status !== 'cancelled')
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [races]);

    const removeComment = (entryId: string, commentId: string) =>
        setCommentsByEntryId((prev) => ({
            ...prev,
            [entryId]: (prev[entryId] ?? []).filter((c) => c.id !== commentId),
        }));

    const handleDeleteComment = async (entryId: string, commentId: string) => {
        if (!window.confirm('Delete this message?')) return;
        try {
            await trainingService.deleteComment(commentId);
            removeComment(entryId, commentId);
        } catch {
            // ignore
        }
    };

    const handleSendComment = async (entryId: string) => {
        const msg = (inputByEntryId[entryId] || '').trim();
        if (!msg) return;
        try {
            setSendingByEntryId((prev) => ({ ...prev, [entryId]: true }));
            await trainingService.addComment(entryId, msg);
            setInputByEntryId((prev) => ({ ...prev, [entryId]: '' }));
            const updated = await trainingService.getComments(entryId);
            setCommentsByEntryId((prev) => ({ ...prev, [entryId]: updated }));
        } catch {
            // ignore
        } finally {
            setSendingByEntryId((prev) => ({ ...prev, [entryId]: false }));
        }
    };

    /**
     * Comment on a day the trainer hasn't assigned an exercise to yet. There is
     * no entry to key off, so the draft is keyed by date and the backend creates
     * a placeholder entry to host the thread. The created comment carries that
     * entry's id, so the thread is grafted into local state instead of calling
     * loadWeek() — a reload here would rebuild editableDays and discard any
     * unsaved exercise edits the trainer has in progress.
     */
    const handleSendDayComment = async (date: string, draftKey: string) => {
        const msg = (inputByEntryId[draftKey] || '').trim();
        if (!msg || !userId) return;
        try {
            setSendingByEntryId((prev) => ({ ...prev, [draftKey]: true }));
            const created = await trainingService.addDayComment(userId, date, msg);
            setInputByEntryId((prev) => ({ ...prev, [draftKey]: '' }));
            setCommentsByEntryId((prev) => ({
                ...prev,
                [created.entryId]: [...(prev[created.entryId] ?? []), created],
            }));
            // Attach the placeholder to its day so later messages on this day
            // take the entry-keyed path instead of creating another thread.
            setWeekData((prev) =>
                prev
                    ? {
                        ...prev,
                        days: prev.days.map((d) =>
                            d.date === date && !d.entries.some((e) => e.id === created.entryId)
                                ? { ...d, entries: [...d.entries, placeholderEntry(created, date, userId)] }
                                : d,
                        ),
                    }
                    : prev,
            );
            // Carry the placeholder id onto the day's blank row so filling it in
            // converts that entry (keeping this thread) instead of creating a
            // second one alongside it.
            setEditableDays((prev) => {
                const sessions = prev[date];
                if (!sessions || sessions.length !== 1 || sessions[0].id) return prev;
                return { ...prev, [date]: [{ ...sessions[0], id: created.entryId }] };
            });
        } catch (error: any) {
            const message = error?.response?.data?.message;
            setSnackbar({
                message: Array.isArray(message) ? message.join(', ') : message || 'Failed to add comment.',
                type: 'error',
            });
        } finally {
            setSendingByEntryId((prev) => ({ ...prev, [draftKey]: false }));
        }
    };

    const handlePrevWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentWeekStart(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentWeekStart(newDate);
    };

    const handleSessionChange = (date: string, index: number, field: keyof EditableSession, value: string) => {
        setEditableDays((prev) => {
            const list = (prev[date] || []).map((s, i) => (i === index ? { ...s, [field]: value } : s));
            return { ...prev, [date]: list };
        });
    };

    // Picking "＋ Add category" opens the inline prompt instead of setting a value.
    const handleSportSelect = (date: string, index: number, value: string) => {
        if (value === ADD_CATEGORY) {
            setNewCategoryLabel('');
            setAddingCategoryFor({ date, index });
            return;
        }
        handleSessionChange(date, index, 'sport', value);
    };

    const handleCreateCategory = async () => {
        const label = newCategoryLabel.trim();
        if (!label || !addingCategoryFor) return;
        try {
            setIsSavingCategory(true);
            const created = await categoryService.create(label);
            setCategories((prev) => [...prev, created]);
            // Select the new category on the cell that opened the prompt.
            handleSessionChange(addingCategoryFor.date, addingCategoryFor.index, 'sport', created.key);
            setAddingCategoryFor(null);
            setNewCategoryLabel('');
        } catch (error: any) {
            setSnackbar({ message: error.response?.data?.message || 'Failed to add category.', type: 'error' });
        } finally {
            setIsSavingCategory(false);
        }
    };

    const handleDeleteCategory = async (category: ExerciseCategory) => {
        if (!window.confirm(`Delete the "${category.label}" category?`)) return;
        try {
            await categoryService.remove(category.id);
            setCategories((prev) => prev.filter((c) => c.id !== category.id));
            setSnackbar({ message: 'Category deleted.', type: 'success' });
        } catch (error: any) {
            setSnackbar({ message: error.response?.data?.message || 'Failed to delete category.', type: 'error' });
        }
    };

    const handleAddSession = (date: string) => {
        setEditableDays((prev) => {
            const list = [...(prev[date] || []), blankSession()];
            return { ...prev, [date]: list };
        });
    };

    const handleRemoveSession = (date: string, index: number) => {
        setEditableDays((prev) => {
            const list = (prev[date] || []).filter((_, i) => i !== index);
            return { ...prev, [date]: list.length === 0 ? [blankSession()] : list };
        });
    };

    const handleCopyFromLastWeek = async () => {
        if (!userId || !weekData) return;

        try {
            setIsCopying(true);
            const previousWeekStart = new Date(currentWeekStart);
            previousWeekStart.setDate(previousWeekStart.getDate() - 7);
            const previousWeekDateParam = toDateOnlyString(previousWeekStart);

            const previousWeek = await trainingService.getUserWeek(userId, previousWeekDateParam);

            setEditableDays((prev) => {
                const next = { ...prev };

                weekData.days.forEach((currentDay, index) => {
                    if (!currentDay.isEditableForTrainer) return;

                    const previousDay = previousWeek.days[index];
                    const sessions = (previousDay?.entries ?? []).map((e) => ({
                        // No id — these are new sessions copied into the current week.
                        title: e.title || '',
                        description: e.description || '',
                        activityLink: e.activityLink || '',
                        sport: e.sport || '',
                    }));
                    next[currentDay.date] = sessions.length > 0 ? sessions : [blankSession()];
                });

                return next;
            });

            setSnackbar({ message: 'Exercises copied from last week. Review and save this week.', type: 'success' });
        } catch (error: any) {
            const message = error?.response?.data?.message;
            const errorText = Array.isArray(message) ? message.join(', ') : message || 'Failed to copy from last week.';
            setSnackbar({ message: errorText, type: 'error' });
        } finally {
            setIsCopying(false);
        }
    };

    /** Returns whether the save succeeded. */
    const handleSaveWeek = async (): Promise<boolean> => {
        if (!weekData) return false;

        const days: Array<{ id?: string; date: string; title?: string; description?: string; activityLink?: string; sport?: string }> = [];
        for (const day of weekData.days) {
            if (!day.isEditableForTrainer) continue;
            const sessions = editableDays[day.date] || [];
            for (const s of sessions) {
                days.push({
                    id: s.id,
                    date: day.date,
                    title: (s.title || '').trim(),
                    description: s.description || '',
                    activityLink: s.activityLink || '',
                    sport: s.sport || '',
                });
            }
        }

        try {
            setIsSaving(true);
            const updated = await trainingService.upsertUserWeek(userId, weekDateParam, { days, programLabel });
            setWeekData(updated);

            const newEditable: Record<string, EditableSession[]> = {};
            updated.days.forEach((day) => {
                newEditable[day.date] = sessionsFromDay(day);
            });
            setEditableDays(newEditable);
            setSnackbar({ message: 'Weekly training plan saved.', type: 'success' });
            return true;
        } catch (error: any) {
            const message = error?.response?.data?.message;
            const errorText = Array.isArray(message) ? message.join(', ') : message || 'Failed to save weekly plan.';
            setSnackbar({ message: errorText, type: 'error' });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const renderSessionRow = (day: TrainingDayView, session: EditableSession, index: number) => {
        const isDisabled = !day.isEditableForTrainer;
        return (
            <div className="space-y-2 relative">
                {!isDisabled && (editableDays[day.date]?.length ?? 0) > 1 && (
                    <button
                        type="button"
                        onClick={() => handleRemoveSession(day.date, index)}
                        title="Remove session"
                        className="absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-white border border-slate-300 text-slate-400 hover:text-red-500 hover:border-red-300 shadow-sm"
                    >
                        <i className="fa-solid fa-xmark text-[11px]"></i>
                    </button>
                )}
                {session.completed && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-cyan-100 text-cyan-700 border-cyan-200">
                        <i className="fa-solid fa-check text-[10px]"></i>
                        Completed by athlete
                        {session.durationHours != null && ` · ${session.durationHours}h`}
                    </span>
                )}
                <select
                    disabled={isDisabled}
                    value={session.sport}
                    onChange={(e) => handleSportSelect(day.date, index, e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                    <option value="">— Sport —</option>
                    {categories.map((c) => (
                        <option key={c.id} value={c.key}>{c.emoji ? `${c.emoji} ` : ''}{c.label}</option>
                    ))}
                    {/* Fallback: if this session's sport is no longer in the list
                        (e.g. a deleted custom one), still show it so it isn't lost. */}
                    {session.sport && !categories.some((c) => c.key === session.sport) && (
                        <option value={session.sport}>{session.sport}</option>
                    )}
                    <option value={ADD_CATEGORY}>＋ Add category…</option>
                </select>
                {addingCategoryFor?.date === day.date && addingCategoryFor?.index === index && (
                    <div className="mt-2 flex items-center gap-2">
                        <input
                            type="text"
                            autoFocus
                            value={newCategoryLabel}
                            onChange={(e) => setNewCategoryLabel(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                            placeholder="New category name"
                            className="flex-1 rounded-lg border border-cyan-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <button
                            type="button"
                            disabled={isSavingCategory || !newCategoryLabel.trim()}
                            onClick={handleCreateCategory}
                            className="px-3 py-2 rounded-lg bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-500 disabled:opacity-50"
                        >
                            {isSavingCategory ? '…' : 'Add'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setAddingCategoryFor(null)}
                            className="px-2 py-2 text-slate-400 hover:text-slate-600 text-sm"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                )}
                <textarea
                    rows={2}
                    disabled={isDisabled}
                    placeholder={isDisabled ? 'Past date - read only' : 'Exercise description'}
                    value={session.description}
                    onChange={(e) => handleSessionChange(day.date, index, 'description', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 bg-white resize-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                />
            </div>
        );
    };

    const renderActivityLinkAndComments = (day: TrainingDayView, session: EditableSession, index: number) => {
        const isDisabled = !day.isEditableForTrainer;
        const entryId = session.id;
        const entryComments = entryId ? (commentsByEntryId[entryId] ?? []) : [];
        const previewComments = entryComments.slice(-2);
        // Unsaved rows have no entry id, so the draft is keyed by date and row
        // index (so two blank rows don't share one draft); sending then creates
        // the entry that hosts the thread.
        const commentKey = entryId ?? `${day.date}#${index}`;
        const sendComment = () => (entryId ? handleSendComment(entryId) : handleSendDayComment(day.date, commentKey));

        return (
            <div className="flex flex-col gap-3">
                {/* No exercise can be assigned on a rest day, so an activity
                    link would be meaningless there — only the thread shows. */}
                {!day.isRestDay && (
                    <input
                        type="text"
                        disabled={isDisabled}
                        value={session.activityLink}
                        onChange={(e) => handleSessionChange(day.date, index, 'activityLink', e.target.value)}
                        placeholder={isDisabled ? 'Not editable' : 'Paste activity link'}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    />
                )}

                {/* Rendered even with no entry yet, so the trainer can leave
                    tips on a day before assigning an exercise. */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 flex flex-col gap-2 px-3 py-2.5">
                    {entryId && entryComments.length > 2 && (
                        <button
                            onClick={() => setModalEntryId(entryId)}
                            title="View full conversation"
                            className="self-end w-7 h-7 flex items-center justify-center rounded-full bg-cyan-50 hover:bg-cyan-100 text-cyan-500 border border-cyan-200 transition-colors"
                        >
                            <i className="fa-solid fa-clock-rotate-left text-xs"></i>
                        </button>
                    )}
                    {previewComments.length === 0 ? (
                        <p className="text-xs text-slate-400 italic py-1">No comments yet</p>
                    ) : (
                        previewComments.map((c) => (
                            <div key={c.id} className={`flex flex-col gap-0.5 ${c.userId === currentUser?.id ? 'items-end' : 'items-start'}`}>
                                <span className="text-[10px] text-slate-400">{c.userId === currentUser?.id ? 'You' : c.senderName}{c.userId === currentUser?.id && (<button onClick={() => handleDeleteComment(entryId!, c.id)} title="Delete message" className="ml-1.5 text-slate-400 hover:text-red-500"><i className="fa-solid fa-trash-can"></i></button>)}</span>
                                <span className={`text-xs px-2.5 py-1.5 rounded-xl max-w-[90%] leading-relaxed ${c.userId === currentUser?.id ? 'bg-cyan-500 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                                    {linkify(c.message)}
                                </span>
                            </div>
                        ))
                    )}
                    <div className="flex items-center gap-2 mt-1">
                        <input
                            type="text"
                            id={`comment-input-${commentKey}`}
                            value={inputByEntryId[commentKey] || ''}
                            onChange={(e) => setInputByEntryId((prev) => ({ ...prev, [commentKey]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') sendComment(); }}
                            placeholder="Write a comment…"
                            className="flex-1 text-base md:text-xs rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 bg-white placeholder-slate-400"
                        />
                        <button
                            onClick={sendComment}
                            disabled={!inputByEntryId[commentKey]?.trim() || sendingByEntryId[commentKey]}
                            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            {sendingByEntryId[commentKey]
                                ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                : <i className="fa-solid fa-paper-plane text-xs"></i>
                            }
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background">
            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}

            {/* Six controls cannot share one row at 375px. On mobile this is two
                rows — back/menu/athlete/save, then the week stepper — collapsing
                back to the original single row from md. */}
            <header className="bg-primary border-b border-cyan-600 px-3 md:px-8 py-2.5 md:py-0 md:h-24 md:flex md:items-center shadow-md z-20 sticky top-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-4 w-full md:justify-between">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                        <button
                            onClick={() => navigate('/')}
                            aria-label="Back"
                            className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all border-2 border-white/40"
                        >
                            <i className="fa-solid fa-arrow-left"></i>
                        </button>
                        {/* Back only reaches the user list, so the drawer still needs
                            its own trigger to get to Calendars or the Library. */}
                        <MenuButton />
                        <div className="bg-white/10 backdrop-blur-md px-2.5 md:px-6 py-1.5 md:py-2 rounded-lg border border-white/20 min-w-0 flex-1 md:flex-none">
                            <h1 className="text-sm md:text-xl font-bold text-white truncate">
                                {weekData
                                    ? `${weekData.user.firstName} ${weekData.user.lastName} - Weekly Training`
                                    : 'Weekly Training'}
                            </h1>
                            {/* Upcoming races sit on the email line so the header keeps its height. */}
                            <div className="hidden md:flex items-center gap-1.5 text-cyan-100 text-xs whitespace-nowrap">
                                <span className="flex-shrink-0">
                                    {weekData?.user.email} {weekData?.user.restDay ? `| Rest day: ${weekData.user.restDay}` : ''}
                                </span>
                                {/* Next 3 only, or a long list would push the week stepper off the row. */}
                                {upcomingRaces.slice(0, 3).map((race) => (
                                    <span
                                        key={race.id}
                                        title={race.location || undefined}
                                        className="inline-flex items-center gap-1 flex-shrink-0 text-[10px] px-1.5 rounded bg-white/90 text-red-700 font-bold"
                                    >
                                        <i className="fa-solid fa-flag-checkered text-[9px]"></i>
                                        {race.raceType} · {new Date(`${race.date}T00:00:00`).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={handleSaveWeek}
                            disabled={isSaving || loading || isCopying}
                            className="md:hidden flex items-center gap-1.5 px-2.5 py-1.5 flex-shrink-0 bg-white text-primary rounded font-bold shadow-md disabled:opacity-60"
                        >
                            <i className="fa-solid fa-floppy-disk text-sm"></i>
                            <span className="text-[11px]">{isSaving ? 'Saving...' : 'Save'}</span>
                        </button>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            onClick={handlePrevWeek}
                            aria-label="Previous week"
                            className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white border-2 border-white/40"
                        >
                            <i className="fa-solid fa-arrow-left text-sm"></i>
                        </button>
                        <div className="bg-white/10 backdrop-blur-md px-2 md:px-4 py-1.5 md:py-2 rounded-lg border border-white/20 flex items-center justify-center md:justify-start gap-1.5 md:gap-3 min-w-0 flex-1 md:flex-none">
                            <span className="text-white text-xs md:text-sm font-bold truncate">
                                {weekData ? `${formatDateNumber(weekData.weekStart)} - ${formatDateNumber(weekData.weekEnd)} ${new Date(`${weekData.weekEnd}T00:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' })}` : 'Loading week...'}
                            </span>
                            <span className="bg-white text-primary text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded shadow-sm whitespace-nowrap flex-shrink-0">
                                Week {getWeekNumber(currentWeekStart)}
                            </span>
                        </div>
                        <button
                            onClick={handleNextWeek}
                            aria-label="Next week"
                            className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white border-2 border-white/40"
                        >
                            <i className="fa-solid fa-arrow-right text-sm"></i>
                        </button>
                        <button
                            onClick={handleSaveWeek}
                            disabled={isSaving || loading || isCopying}
                            className="hidden md:flex items-center ml-3 px-4 py-2.5 bg-white text-primary rounded font-bold hover:bg-slate-50 transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <i className="fa-solid fa-floppy-disk mr-2"></i>
                            {isSaving ? 'Saving...' : 'Save Week'}
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-slate-50">
                <div className="max-w-[1600px] mx-auto mb-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Program:</label>
                        <input
                            type="text"
                            value={programLabel}
                            onChange={(e) => setProgramLabel(e.target.value)}
                            placeholder='e.g. "Program 1 - Week 4"'
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 bg-white shadow-sm focus:outline-none focus:border-cyan-400 w-full md:w-auto md:min-w-[260px]"
                        />
                        <div className="relative" data-categories-menu>
                            <button
                                type="button"
                                onClick={() => setShowManageCategories((v) => !v)}
                                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 bg-white shadow-sm hover:border-cyan-400"
                            >
                                <i className="fa-solid fa-tags text-cyan-600"></i>
                                Categories
                            </button>
                            {showManageCategories && (
                                // Anchored right on mobile: left-0 with a fixed w-64
                                // pushes the panel past the viewport edge and makes
                                // the whole page scroll sideways.
                                <div className="absolute left-0 top-full mt-1 z-30 w-64 max-md:left-auto max-md:right-0 max-md:max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                                    <p className="px-4 py-1 text-xs font-bold text-slate-400 uppercase tracking-wider">Exercise categories</p>
                                    <div className="max-h-64 overflow-y-auto">
                                        {categories.map((c) => (
                                            <div key={c.id} className="flex items-center justify-between px-4 py-1.5 hover:bg-slate-50">
                                                <span className="text-sm text-slate-700">{c.emoji ? `${c.emoji} ` : ''}{c.label}</span>
                                                {c.isSeeded ? (
                                                    <span className="text-[10px] text-slate-300 uppercase">built-in</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteCategory(c)}
                                                        className="text-slate-400 hover:text-red-500"
                                                        title="Delete category"
                                                    >
                                                        <i className="fa-solid fa-trash text-xs"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleCopyFromLastWeek}
                        disabled={loading || isSaving || isCopying}
                        className="px-3 py-2 rounded-lg border border-cyan-300 bg-white text-cyan-700 text-sm font-semibold shadow-sm hover:bg-cyan-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {isCopying ? 'Copying...' : 'Copy from last week'}
                    </button>
                </div>
                <div className="max-w-[1600px] mx-auto bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
                    <div className="hidden md:grid grid-cols-12 gap-6 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-blue-600 text-xs font-black text-white uppercase tracking-wider">
                        <div className="col-span-2">Day & Date</div>
                        <div className="col-span-6">Training Plan</div>
                        <div className="col-span-4">Activity Link</div>
                    </div>

                    {loading ? (
                        <div className="py-20 text-center text-slate-400">
                            <i className="fa-solid fa-spinner fa-spin text-3xl"></i>
                        </div>
                    ) : (
                        weekData?.days.map((day) => {
                            const sessions = editableDays[day.date] || [];
                            return (
                                <div
                                    key={day.date}
                                    className={`p-3 md:p-5 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-start border-b border-slate-100 ${day.isToday ? 'bg-cyan-50/40' : 'hover:bg-slate-50'}`}
                                >
                                    <div className={`md:col-span-2 flex flex-wrap md:flex-col items-center md:items-start gap-x-2 gap-y-1 md:gap-0 pt-1 ${day.isToday ? 'pl-2' : ''}`}>
                                        <span className={`text-xs font-bold uppercase tracking-wider mb-1 ${day.isToday ? 'text-primary' : 'text-slate-500'}`}>
                                            {day.isToday ? 'Today' : day.dayName}
                                        </span>
                                        <div className="flex items-baseline gap-1">
                                            <span className={`text-xl font-bold ${day.isToday ? 'text-primary' : 'text-slate-800'}`}>
                                                {formatDateNumber(day.date)}
                                            </span>
                                            <span className="text-sm text-slate-400">{formatMonthYear(day.date)}</span>
                                        </div>
                                        {day.isPast && !day.isRestDay && (
                                            <span className="text-[11px] mt-1 px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">
                                                Past date
                                            </span>
                                        )}
                                        {racesByDate[day.date]?.map((race) => (
                                            <span
                                                key={race.id}
                                                title={`${race.raceType}${race.location ? ` — ${race.location}` : ''}`}
                                                className="inline-flex items-center gap-1 text-[11px] mt-1 px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold border border-red-200 max-w-full"
                                            >
                                                <i className="fa-solid fa-flag-checkered text-[10px]"></i>
                                                <span className="truncate">{race.raceType}</span>
                                            </span>
                                        ))}
                                    </div>

                                    <div className="md:col-span-10">
                                        {day.isRestDay ? (
                                            <div className="grid grid-cols-1 md:grid-cols-10 gap-3 md:gap-6">
                                                <div className="md:col-span-6">
                                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 min-h-[118px]">
                                                        <div className="font-bold flex items-center gap-2 mb-1">
                                                            <i className="fa-solid fa-lock"></i>
                                                            Rest day locked
                                                        </div>
                                                        <p>Trainer cannot assign exercise on member rest day.</p>
                                                    </div>
                                                </div>
                                                {/* The athlete can still ask for tips on a rest day, so
                                                    surface that thread instead of hiding it. */}
                                                <div className="md:col-span-4">
                                                    {renderActivityLinkAndComments(
                                                        day,
                                                        { ...blankSession(), id: day.entries[0]?.id },
                                                        0,
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {sessions.map((session, index) => (
                                                    <div
                                                        key={session.id ?? `new-${index}`}
                                                        className={`grid grid-cols-1 md:grid-cols-10 gap-3 md:gap-6 ${index > 0 ? 'mt-4 pt-4 border-t border-dashed border-slate-200' : ''}`}
                                                    >
                                                        <div className="md:col-span-6">
                                                            {renderSessionRow(day, session, index)}
                                                        </div>
                                                        <div className="md:col-span-4">
                                                            {renderActivityLinkAndComments(day, session, index)}
                                                        </div>
                                                    </div>
                                                ))}
                                                {day.isEditableForTrainer && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddSession(day.date)}
                                                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-cyan-300 text-cyan-600 hover:bg-cyan-50 text-xs font-semibold"
                                                    >
                                                        <i className="fa-solid fa-plus"></i>
                                                        Add session
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {pendingNotification && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-cyan-500 to-blue-500">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <i className="fa-solid fa-bell text-white text-sm"></i>
                            </div>
                            <h3 className="text-white font-bold text-sm">
                                {pendingNotification.userId === userId ? 'Notification for another week' : 'Notification for another athlete'}
                            </h3>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                {pendingNotification.message}
                            </p>
                            <p className="text-sm text-slate-700">
                                Save your changes to{' '}
                                <span className="font-semibold">
                                    {weekData ? `${weekData.user.firstName} ${weekData.user.lastName}'s` : 'this'}
                                </span>{' '}
                                week before opening it?
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
                            <button
                                onClick={() => setPendingNotification(null)}
                                disabled={isSaving}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveAndOpenNotification}
                                disabled={isSaving}
                                className="px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-500 hover:bg-cyan-600 text-white transition-colors flex items-center gap-2 disabled:opacity-60"
                            >
                                {isSaving && <i className="fa-solid fa-spinner fa-spin text-xs"></i>}
                                Save &amp; open
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Comment modal */}
            {modalEntryId && currentUser && (() => {
                const entry = weekData?.days
                    .flatMap((d) => d.entries)
                    .find((e) => e.id === modalEntryId);
                return (
                    <TrainingCommentModal
                        entryId={modalEntryId}
                        entryTitle={entry?.title ?? ''}
                        currentUserId={currentUser.id}
                        onClose={() => setModalEntryId(null)}
                        onDeleted={(id) => removeComment(modalEntryId, id)}
                    />
                );
            })()}
        </div>
    );
};

export default UserTrainingPlan;
